import React, { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import TextField from "@mui/material/TextField";
import { useTheme } from "@mui/material/styles";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { useMaterialUIController } from "context";
import AlertService from "services/alertService";

function AlertHistory() {
  const theme = useTheme();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedAlertIndex, setSelectedAlertIndex] = useState(0);

  useEffect(() => {
    const fetchAlertHistory = async () => {
      try {
        setLoading(true);
        setError(null);
        const alertHistory = await AlertService.getAlertHistory(1000);
        setAlerts(alertHistory);
        if (alertHistory.length > 0) {
          setSelectedAlertIndex(0);
        }
      } catch (err) {
        console.error("Error fetching alert history:", err);
        setError("Failed to load alert history");
      } finally {
        setLoading(false);
      }
    };

    fetchAlertHistory();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch (e) {
      return dateString;
    }
  };

  const selectedAlert = alerts[selectedAlertIndex];
  const alertJsonText = selectedAlert ? JSON.stringify(selectedAlert.data, null, 2) : "";

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={6} mb={3} px={2}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card
              sx={{
                height: "auto",
                backgroundColor: darkMode 
                  ? theme.palette.background.card 
                  : theme.palette.background.paper,
                borderRadius: "12px",
                boxShadow: 3,
                p: 3,
              }}
            >
              {/* Header */}
              <MDBox
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                px={3}
                py={2}
                sx={{
                  backgroundColor: theme.palette.info.main,
                  borderRadius: "8px",
                }}
              >
                <MDTypography variant="h6" fontWeight="bold" color="white">
                  Alert History
                </MDTypography>
                <MDTypography variant="body2" color="white" sx={{ opacity: 0.9 }}>
                  {alerts.length} {alerts.length === 1 ? "alert" : "alerts"} (max 1000)
                </MDTypography>
              </MDBox>

              <Divider 
                sx={{ 
                  my: 2, 
                  bgcolor: darkMode ? "grey.700" : "grey.600" 
                }} 
              />

              {/* Content */}
              {loading ? (
                <MDBox 
                  display="flex" 
                  justifyContent="center" 
                  alignItems="center" 
                  minHeight="400px"
                >
                  <CircularProgress color="info" />
                </MDBox>
              ) : error ? (
                <MDBox 
                  display="flex" 
                  justifyContent="center" 
                  alignItems="center" 
                  minHeight="400px"
                >
                  <MDTypography variant="h6" color="error">
                    {error}
                  </MDTypography>
                </MDBox>
              ) : alerts.length === 0 ? (
                <MDBox 
                  display="flex" 
                  justifyContent="center" 
                  alignItems="center" 
                  minHeight="400px"
                >
                  <MDTypography variant="body1" color="textSecondary">
                    No alerts found
                  </MDTypography>
                </MDBox>
              ) : (
                <Grid container spacing={3}>
                  {/* Left Side - Alert List */}
                  <Grid item xs={12} md={4}>
                    <MDBox
                      sx={{
                        backgroundColor: darkMode 
                          ? theme.palette.background.default 
                          : theme.palette.background.default,
                        borderRadius: "10px",
                        p: 2,
                        maxHeight: "75vh",
                        overflowY: "auto",
                        boxShadow: 2,
                      }}
                    >
                      <MDTypography variant="h6" mb={2} color={darkMode ? "white" : "text"}>
                        Alerts ({alerts.length})
                      </MDTypography>
                      {alerts.map((alert, index) => (
                        <MDBox
                          key={alert.id || index}
                          onClick={() => setSelectedAlertIndex(index)}
                          sx={{
                            p: 2,
                            mb: 1,
                            borderRadius: "8px",
                            cursor: "pointer",
                            border: selectedAlertIndex === index
                              ? `2px solid ${theme.palette.info.main}`
                              : darkMode 
                                ? "1px solid rgba(255, 255, 255, 0.1)" 
                                : "1px solid rgba(0, 0, 0, 0.1)",
                            backgroundColor: selectedAlertIndex === index
                              ? (darkMode ? "rgba(25, 118, 210, 0.2)" : "rgba(25, 118, 210, 0.1)")
                              : (darkMode ? "rgba(255, 255, 255, 0.03)" : "transparent"),
                            "&:hover": {
                              backgroundColor: darkMode 
                                ? "rgba(255, 255, 255, 0.08)" 
                                : "rgba(0, 0, 0, 0.05)",
                            },
                          }}
                        >
                          <MDTypography 
                            variant="caption" 
                            color={darkMode ? "white" : "textSecondary"}
                            fontWeight="medium"
                            display="block"
                            mb={0.5}
                          >
                            {formatDate(alert.created_at)}
                          </MDTypography>
                          <MDTypography 
                            variant="caption" 
                            color={darkMode ? "white" : "text"}
                            sx={{
                              fontFamily: "monospace",
                              fontSize: "11px",
                              opacity: 0.8,
                            }}
                          >
                            {alert.data?.action || "N/A"} - {alert.data?.account || "N/A"}
                          </MDTypography>
                        </MDBox>
                      ))}
                    </MDBox>
                  </Grid>

                  {/* Right Side - Alert Details */}
                  <Grid item xs={12} md={8}>
                    <MDBox
                      sx={{
                        backgroundColor: darkMode 
                          ? theme.palette.background.default 
                          : theme.palette.background.default,
                        borderRadius: "10px",
                        p: 2,
                        minHeight: "75vh",
                        maxHeight: "75vh",
                        overflowY: "auto",
                        boxShadow: 2,
                      }}
                    >
                      {selectedAlert ? (
                        <>
                          <MDBox mb={2}>
                            <MDTypography variant="h6" mb={1} color={darkMode ? "white" : "text"}>
                              Alert Details
                            </MDTypography>
                            <MDTypography 
                              variant="caption" 
                              color={darkMode ? "white" : "textSecondary"}
                            >
                              {formatDate(selectedAlert.created_at)} • ID: {selectedAlert.id?.substring(0, 8)}...
                            </MDTypography>
                          </MDBox>
                          <TextField
                            fullWidth
                            multiline
                            minRows={25}
                            maxRows={40}
                            value={alertJsonText}
                            variant="outlined"
                            placeholder="No alert selected"
                            InputProps={{
                              readOnly: true,
                            }}
                            sx={{
                              fontFamily: "monospace",
                              backgroundColor: darkMode 
                                ? theme.palette.background.card 
                                : theme.palette.background.paper,
                              fontSize: "13px",
                              "& .MuiOutlinedInput-root": {
                                color: darkMode ? theme.palette.text.primary : theme.palette.text.primary,
                                "& fieldset": {
                                  borderColor: darkMode ? theme.palette.grey[700] : theme.palette.grey[300],
                                },
                                "&:hover fieldset": {
                                  borderColor: darkMode ? theme.palette.grey[600] : theme.palette.grey[400],
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: theme.palette.info.main,
                                },
                              },
                              "& .MuiInputBase-input": {
                                color: darkMode ? "#ffffff" : theme.palette.text.primary,
                                "&::placeholder": {
                                  color: darkMode ? theme.palette.text.secondary : theme.palette.text.secondary,
                                  opacity: 1,
                                },
                              },
                            }}
                          />
                        </>
                      ) : (
                        <MDBox 
                          display="flex" 
                          justifyContent="center" 
                          alignItems="center" 
                          height="100%"
                        >
                          <MDTypography variant="body1" color="textSecondary">
                            Select an alert to view details
                          </MDTypography>
                        </MDBox>
                      )}
                    </MDBox>
                  </Grid>
                </Grid>
              )}
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default AlertHistory;
